import { Component, inject, input } from '@angular/core';
import { UwIcon } from '../../ui/icon/uw-icon';
import { UwBadge } from '../../ui/badge/uw-badge';
import { UwAvatar } from '../../ui/avatar/uw-avatar';
import { UwButton } from '../../ui/button/uw-button';
import { CopilotStore } from '../../../core/stores/copilot.store';
import type { AuthorityCheck } from '../../../core/models/evidence.model';
import { UwMoneyPipe, UwPercentPipe } from '../../pipes/format.pipes';

@Component({
  selector: 'uw-authority-evidence',
  standalone: true,
  imports: [UwIcon, UwBadge, UwAvatar, UwButton, UwMoneyPipe, UwPercentPipe],
  template: `
    @if (authority(); as a) {
      <div class="space-y-3">
        <div class="flex items-start justify-between gap-3 rounded-lg border border-warning/30 bg-warning-soft/50 p-3">
          <div class="flex items-start gap-2.5">
            <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-warning-soft text-warning">
              <uw-icon name="shield" [size]="16" />
            </span>
            <div>
              <p class="text-[12.5px] font-semibold text-primary">{{ a.title }}</p>
              <p class="mt-0.5 text-[11px] text-warning">{{ a.ruleTriggered }}</p>
            </div>
          </div>
          <uw-badge tone="warning">Exceeds by {{ a.exceedsPct | uwPercent }}</uw-badge>
        </div>

        <div class="grid grid-cols-2 gap-2">
          <div class="rounded-lg border border-line bg-surface p-3">
            <p class="typo-label">Your authority limit</p>
            <p class="mt-1 typo-mono text-[15px] font-bold text-primary">{{ a.authorityLimit | uwMoney }}</p>
          </div>
          <div class="rounded-lg border border-line bg-surface p-3">
            <p class="typo-label">Requested TIV</p>
            <p class="mt-1 typo-mono text-[15px] font-bold text-danger">{{ a.requested | uwMoney }}</p>
          </div>
        </div>

        <div class="flex items-center gap-3 rounded-lg border border-line bg-surface p-3">
          <uw-avatar [initials]="a.approverInitials" [label]="a.approver" />
          <div class="min-w-0 flex-1">
            <p class="text-[12px] font-semibold text-primary">{{ a.approver }}</p>
            <p class="text-[11px] text-text-muted">{{ a.approverRole }} · required for approval</p>
          </div>
          <uw-badge tone="info" icon="clock">Referral</uw-badge>
        </div>

        <p class="rounded-lg bg-surface-sunken px-3 py-2.5 text-[11.5px] leading-relaxed text-secondary">
          {{ a.note }}
        </p>

        <uw-button variant="secondary" size="sm" icon="arrow-up" (click)="openActions()">View referral memo in Action Hub</uw-button>
      </div>
    }
  `,
})
export class UwAuthorityEvidence {
  readonly authority = input<AuthorityCheck | null>(null);

  private readonly copilot = inject(CopilotStore);

  protected openActions(): void {
    this.copilot.setView('account');
    this.copilot.setActionBanner('Referral memo drafted and ready for review');
  }
}
